import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { BookOpen, Trophy, AlertTriangle, ChevronDown, ChevronUp, Coins, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';

/* ────── Rules Data ────── */
const RULES_DATA = [
  {
    name: 'Jodi', nameHi: 'जोड़ी',
    descHi: '00 से 99 तक कोई भी 2 अंक का नंबर चुनो। अगर जीतने वाला नंबर बिल्कुल मैच करे तो जीत!',
    desc: 'Pick any 2-digit number (00-99). Exact match wins.',
    example: '₹10 पर 47 लगाया, रिज़ल्ट 47 आया → आप जीते!',
    multiplierKey: 'Jodi', color: '#FF2A6D',
  },
  {
    name: 'Hurf', nameHi: 'हर्फ़',
    descHi: '0-9 में से एक अंक चुनो। Ander(A)=पहला अंक, Bahar(B)=दूसरा, AB=दोनों।',
    desc: 'Pick a digit (0-9). A=left digit, B=right digit, AB=both.',
    example: 'रिज़ल्ट 47 → Ander=4, Bahar=7। 4 पर Ander लगाया = जीत!',
    multiplierKey: 'Hurf', color: '#05D9E8',
  },
  {
    name: 'Cross', nameHi: 'क्रॉस',
    descHi: '2+ सिंगल डिजिट चुनो → सिस्टम सभी 2-अंक कॉम्बिनेशन बनाएगा।',
    desc: 'Pick 2+ digits. System creates all 2-digit combos.',
    example: '4,7 चुने → 44,47,74,77 बने। कोई मैच = जीत!',
    multiplierKey: 'Cross', color: '#FFD700',
  },
  {
    name: 'Single', nameHi: 'सिंगल',
    descHi: '0-9 में से एक अंक। रिज़ल्ट का आखिरी अंक मैच करे तो जीत!',
    desc: 'Pick digit 0-9. Last digit of result matches = win.',
    example: '7 पर लगाया, रिज़ल्ट 47 → आखिरी अंक 7 = जीत!',
    multiplierKey: 'Single', color: '#01FFC3',
  },
  {
    name: 'Patti', nameHi: 'पट्टी',
    descHi: '3 अंक का नंबर (000-999)। डिजिट-सम मैच = सबसे बड़ा इनाम!',
    desc: '3-digit number (000-999). Digit-sum match wins big!',
    example: '470 डाला, मैच हुआ → बड़ी जीत!',
    multiplierKey: 'Patti', color: '#AB47BC',
  },
];

/* ────── Component ────── */
export default function RulesPage() {
  const [activeTab, setActiveTab] = useState('results');
  const [multipliers, setMultipliers] = useState({});
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loadingResults, setLoadingResults] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    api.get('/game/settings').then(r => setMultipliers(r.data.multipliers || {})).catch(() => {});
  }, []);

  const fetchResults = useCallback(async (date) => {
    setLoadingResults(true);
    try {
      // Use local date components to avoid UTC timezone shift
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const res = await api.get(`/results/by-date?date=${dateStr}`);
      setResults(res.data.results || []);
    } catch { setResults([]); }
    setLoadingResults(false);
  }, []);

  useEffect(() => { fetchResults(selectedDate); }, [selectedDate, fetchResults]);

  const goYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
  };
  const goToday = () => setSelectedDate(new Date());

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const isYesterday = (() => {
    const y = new Date(); y.setDate(y.getDate() - 1);
    return selectedDate.toDateString() === y.toDateString();
  })();

  const formatDate = (d) => d.toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="min-h-screen pb-28 p-4" style={{ background: 'var(--neon-bg)' }}>
      <div className="max-w-2xl mx-auto space-y-4">

        {/* Tabs */}
        <div className="flex gap-2">
          <button data-testid="tab-results" onClick={() => setActiveTab('results')}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
            style={{
              background: activeTab === 'results' ? 'var(--neon-primary)' : 'var(--neon-elevated)',
              color: activeTab === 'results' ? '#000' : 'var(--neon-text-secondary)',
            }}>
            <Trophy size={16} /> Results
          </button>
          <button data-testid="tab-rules" onClick={() => setActiveTab('rules')}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
            style={{
              background: activeTab === 'rules' ? 'var(--neon-primary)' : 'var(--neon-elevated)',
              color: activeTab === 'rules' ? '#000' : 'var(--neon-text-secondary)',
            }}>
            <BookOpen size={16} /> Rules / नियम
          </button>
        </div>

        {/* ═══════ RESULTS TAB ═══════ */}
        {activeTab === 'results' && (
          <>
            {/* Date Selector */}
            <div className="flex gap-2">
              <button data-testid="date-yesterday" onClick={goYesterday}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: isYesterday ? 'var(--neon-secondary)' : 'var(--neon-elevated)',
                  color: isYesterday ? '#fff' : 'var(--neon-text-secondary)',
                }}>
                <ChevronLeft size={14} /> Yesterday
              </button>
              <button data-testid="date-today" onClick={goToday}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: isToday ? 'var(--neon-secondary)' : 'var(--neon-elevated)',
                  color: isToday ? '#fff' : 'var(--neon-text-secondary)',
                }}>
                <CalendarIcon size={14} /> Today
              </button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button data-testid="date-picker-btn"
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
                    style={{
                      background: !isToday && !isYesterday ? 'var(--neon-secondary)' : 'var(--neon-elevated)',
                      color: !isToday && !isYesterday ? '#fff' : 'var(--neon-text-secondary)',
                    }}>
                    <CalendarIcon size={14} /> Select Date
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center"
                  style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <Calendar mode="single" selected={selectedDate}
                    onSelect={(d) => { if (d) { setSelectedDate(d); setCalendarOpen(false); } }}
                    disabled={(d) => d > new Date()}
                    className="rounded-xl" />
                </PopoverContent>
              </Popover>
            </div>

            {/* Selected Date Label */}
            <p className="text-center text-xs font-bold" style={{ color: 'var(--neon-text-muted)' }}>
              {formatDate(selectedDate)}
            </p>

            {/* Results Grid */}
            {loadingResults ? (
              <div className="glass-card rounded-2xl p-10 text-center">
                <p className="text-sm animate-pulse" style={{ color: 'var(--neon-primary)' }}>Loading...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="glass-card rounded-2xl p-10 text-center">
                <Trophy size={32} className="mx-auto mb-2" style={{ color: 'var(--neon-text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--neon-text-muted)' }}>No results for this date</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {results.map((r, idx) => {
                  const hasResult = r.winning_number !== null && r.winning_number !== undefined;
                  return (
                    <div key={idx} data-testid={`result-card-${idx}`}
                      className="glass-card rounded-2xl p-4 text-center transition-all hover:scale-[1.02]"
                      style={{ borderTop: `3px solid ${hasResult ? 'var(--neon-primary)' : 'var(--neon-text-muted)'}` }}>
                      {/* Company Name */}
                      <p className="font-black text-sm mb-1 truncate"
                        style={{ fontFamily: 'var(--font-heading)', color: hasResult ? 'var(--neon-secondary)' : 'var(--neon-text-secondary)' }}>
                        {r.company_name}
                      </p>
                      {/* Time */}
                      {(r.result_time || r.closing_time) && (
                        <p className="text-[10px] flex items-center justify-center gap-1 mb-3"
                          style={{ color: 'var(--neon-text-muted)' }}>
                          <Clock size={10} /> {r.result_time || r.closing_time}
                        </p>
                      )}
                      {/* Winning Number Circle */}
                      {hasResult ? (
                        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
                          style={{
                            background: 'linear-gradient(135deg, var(--neon-elevated), var(--neon-surface))',
                            border: '3px solid var(--neon-accent)',
                            boxShadow: '0 0 20px rgba(255,215,0,0.3)',
                          }}>
                          <span className="text-2xl font-black"
                            style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)' }}>
                            {String(r.winning_number).padStart(2, '0')}
                          </span>
                        </div>
                      ) : (
                        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
                          style={{
                            background: 'var(--neon-elevated)',
                            border: '2px dashed rgba(255,255,255,0.1)',
                          }}>
                          <span className="text-xs font-bold" style={{ color: 'var(--neon-text-muted)' }}>
                            Pending
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ═══════ RULES TAB ═══════ */}
        {activeTab === 'rules' && (
          <>
            {/* General Rules */}
            <div data-testid="general-rules" className="glass-card rounded-2xl p-5">
              <h2 className="font-bold text-base mb-3 flex items-center gap-2"
                style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)' }}>
                <AlertTriangle size={16} /> सामान्य नियम
              </h2>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--neon-text-secondary)' }}>
                <li className="flex gap-2"><span style={{ color: 'var(--neon-primary)' }}>1.</span> पहले बेटिंग ऑप्शन चुनो (Jodi, Hurf, Cross, etc.)</li>
                <li className="flex gap-2"><span style={{ color: 'var(--neon-primary)' }}>2.</span> फिर नंबर चुनो और कंपनी सेलेक्ट करो</li>
                <li className="flex gap-2"><span style={{ color: 'var(--neon-primary)' }}>3.</span> अमाउंट डालो और Bet Place करो</li>
                <li className="flex gap-2"><span style={{ color: 'var(--neon-primary)' }}>4.</span> रिज़ल्ट आने पर सिस्टम ऑटोमैटिक जीत/हार बता देगा</li>
                <li className="flex gap-2"><span style={{ color: 'var(--neon-primary)' }}>5.</span> जीत का पैसा सीधे वॉलेट में आ जाएगा</li>
                <li className="flex gap-2"><span style={{ color: 'var(--neon-secondary)' }}>*</span> Withdraw के लिए पहले Payment Info (UPI) भरना ज़रूरी है</li>
              </ul>
            </div>

            {/* Betting Options */}
            <h2 className="font-bold text-base flex items-center gap-2"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
              <Coins size={16} style={{ color: 'var(--neon-accent)' }} /> बेटिंग ऑप्शन्स
            </h2>

            {RULES_DATA.map((rule, idx) => {
              const isExpanded = expandedIdx === idx;
              const mult = multipliers[rule.multiplierKey] || '—';
              return (
                <div key={idx} data-testid={`rule-card-${rule.name.toLowerCase()}`}
                  className="glass-card rounded-2xl overflow-hidden transition-all"
                  style={{ borderLeft: `3px solid ${rule.color}` }}>
                  <button data-testid={`rule-toggle-${rule.name.toLowerCase()}`}
                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                    className="w-full p-4 flex items-center justify-between text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm"
                        style={{ background: `${rule.color}20`, color: rule.color, fontFamily: 'var(--font-heading)' }}>
                        {rule.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>
                          {rule.name} <span style={{ color: 'var(--neon-text-muted)' }}>/ {rule.nameHi}</span>
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: rule.color }}>Multiplier: {mult}x</p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp size={18} style={{ color: 'var(--neon-text-muted)' }} /> :
                      <ChevronDown size={18} style={{ color: 'var(--neon-text-muted)' }} />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <div className="pt-3">
                        <p className="text-sm" style={{ color: 'var(--neon-text-secondary)' }}>{rule.descHi}</p>
                        <p className="text-xs mt-1" style={{ color: 'var(--neon-text-muted)' }}>{rule.desc}</p>
                      </div>
                      <div className="p-3 rounded-xl" style={{ background: 'var(--neon-elevated)' }}>
                        <p className="text-xs font-bold mb-1" style={{ color: 'var(--neon-accent)' }}>Example:</p>
                        <p className="text-xs" style={{ color: 'var(--neon-text-secondary)' }}>{rule.example}</p>
                      </div>
                      <div className="p-3 rounded-xl flex items-center justify-between"
                        style={{ background: `${rule.color}10`, border: `1px solid ${rule.color}30` }}>
                        <span className="text-xs font-bold" style={{ color: 'var(--neon-text-secondary)' }}>जीत:</span>
                        <span className="text-sm font-black" style={{ color: rule.color, fontFamily: 'var(--font-heading)' }}>
                          Bet x {mult} = Winnings
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* How winning works */}
            <div data-testid="winning-info" className="glass-card rounded-2xl p-5">
              <h2 className="font-bold text-base mb-3 flex items-center gap-2"
                style={{ fontFamily: 'var(--font-heading)', color: '#01FFC3' }}>
                <Trophy size={16} /> जीत कैसे मिलती है?
              </h2>
              <div className="space-y-2 text-sm" style={{ color: 'var(--neon-text-secondary)' }}>
                <p>Admin रिज़ल्ट डिक्लेयर करता है (कंपनी + जीतने वाला नंबर)।</p>
                <p>सिस्टम ऑटोमैटिक सभी बेट्स चेक करता है:</p>
                <ul className="ml-4 space-y-1 text-xs" style={{ color: 'var(--neon-text-muted)' }}>
                  <li>- Jodi: पूरा नंबर मैच → जीत</li>
                  <li>- Single: आखिरी अंक मैच → जीत</li>
                  <li>- Hurf A: पहला अंक मैच → जीत</li>
                  <li>- Hurf B: दूसरा अंक मैच → जीत</li>
                  <li>- Cross: कोई कॉम्बिनेशन मैच → जीत</li>
                </ul>
                <p className="mt-2 text-xs" style={{ color: 'var(--neon-accent)' }}>
                  जीत का पैसा तुरंत आपके वॉलेट में जुड़ जाता है!
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
